const BACKEND_URI = "";

import {
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    Config,
    SimpleAPIResponse,
    HistoryListApiResponse,
    HistoryApiResponse,
    BrowseNotesResponse,
    PatientBriefResponse,
    PatientTimelineResponse,
    PatientDemographics,
    PatientCaseReview,
    GraphPayload
} from "./models";
import { useLogin, getToken, isUsingAppServicesLogin } from "../authConfig";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    // If using login and not using app services, add the id token of the logged in account as the authorization
    if (useLogin && !isUsingAppServicesLogin) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }

    return {};
}

export async function configApi(): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET"
    });

    return (await response.json()) as Config;
}

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (response.status > 299 || !response.ok) {
        throw Error(`Request failed with status ${response.status}`);
    }
    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (parsedResponse.error) {
        throw Error(parsedResponse.error);
    }

    return parsedResponse as ChatAppResponse;
}

export async function chatApi(request: ChatAppRequest, shouldStream: boolean, idToken: string | undefined): Promise<Response> {
    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream";
    }
    const headers = await getHeaders(idToken);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

export async function getDocument(documentId: string, idToken: string | undefined): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/document/${encodeURIComponent(documentId)}`, {
        headers
    });
    if (!response.ok) {
        throw new Error(`Document not found: ${documentId}`);
    }
    return response.json();
}

export async function getCitationContextGraph(assessmentIds: string[], idToken: string | undefined, limit: number = 80): Promise<GraphPayload> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    assessmentIds.forEach(assessmentId => params.append("assessment_ids", assessmentId));
    params.set("limit", String(limit));
    const response = await fetch(`${BACKEND_URI}/graph/citation-context?${params.toString()}`, {
        headers
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch evidence graph: ${response.status}`);
    }
    return response.json();
}

export async function getPatientTrajectoryGraph(consumerId: string, idToken: string | undefined, limit: number = 150): Promise<GraphPayload> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    const response = await fetch(`${BACKEND_URI}/graph/patient/${encodeURIComponent(consumerId)}/trajectory?${params.toString()}`, {
        headers
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch patient trajectory: ${response.status}`);
    }
    return response.json();
}

export interface HighlightEvidenceResponse {
    assessment_id: string;
    highlights: Record<string, string[]>;
    categories_searched: string[];
}

export async function getHighlightEvidence(assessmentId: string, categories: string[], idToken: string | undefined): Promise<HighlightEvidenceResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/highlight-evidence`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
            assessment_id: assessmentId,
            categories: categories
        })
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch highlight evidence: ${response.statusText}`);
    }
    return response.json();
}

export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                console.log("Speech synthesis is not enabled.");
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export function getCitationFilePath(citation: string): string {
    // If there are parentheses at end of citation, remove part in parentheses
    const cleanedCitation = citation.replace(/\s*\(.*?\)\s*$/, "").trim();
    return `${BACKEND_URI}/content/${cleanedCitation}`;
}

export async function uploadFileApi(request: FormData, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/upload", {
        method: "POST",
        headers: await getHeaders(idToken),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(filename: string, idToken: string): Promise<SimpleAPIResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(idToken: string): Promise<string[]> {
    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: await getHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function postChatHistoryApi(item: any, idToken: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch("/chat_history", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(item)
    });

    if (!response.ok) {
        throw new Error(`Posting chat history failed: ${response.statusText}`);
    }

    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getChatHistoryListApi(
    count: number,
    continuationToken: string | undefined,
    idToken: string,
    userId: string
): Promise<HistoryListApiResponse> {
    const headers = await getHeaders(idToken);
    let url = `${BACKEND_URI}/chat_history/sessions?count=${count}&user_id=${encodeURIComponent(userId)}`;
    if (continuationToken) {
        url += `&continuationToken=${continuationToken}`;
    }

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat histories failed: ${response.statusText}`);
    }

    const dataResponse: HistoryListApiResponse = await response.json();
    return dataResponse;
}

export async function getChatHistoryApi(id: string, idToken: string, userId: string): Promise<HistoryApiResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/chat_history/sessions/${id}?user_id=${encodeURIComponent(userId)}`, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }

    const dataResponse: HistoryApiResponse = await response.json();
    return dataResponse;
}

export async function deleteChatHistoryApi(id: string, idToken: string, userId: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/chat_history/sessions/${id}?user_id=${encodeURIComponent(userId)}`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
}
export async function browseNotes(
    patientId: string,
    top: number = 10,
    skip: number = 0,
    sortOrder?: "newest" | "oldest",
    noteTypes?: string[],
    formNames?: string[],
    serviceCodes?: string[],
    clinicalActivities?: string[],
    assessmentScales?: string[],
    servicePrograms?: string[],
    serviceDescriptions?: string[],
    providers?: string[],
    caseManagers?: string[],
    assessmentStaff?: string[],
    severityIndicators?: string[],
    dateRanges?: string[],
    idToken?: string,
    patientIds?: string[]
): Promise<BrowseNotesResponse> {
    const params = new URLSearchParams({
        top: top.toString(),
        skip: skip.toString()
    });
    if (patientIds?.length) {
        params.set("patient_ids", patientIds.join(","));
    } else if (patientId) {
        params.set("patient_id", patientId);
    }

    if (sortOrder) params.append("sort_order", sortOrder);
    if (noteTypes?.length) params.append("noteTypes", noteTypes.join(","));
    if (formNames?.length) params.append("formNames", formNames.join(","));
    if (serviceCodes?.length) params.append("serviceCodes", serviceCodes.join(","));
    if (clinicalActivities?.length) params.append("clinicalActivities", clinicalActivities.join(","));
    if (assessmentScales?.length) params.append("assessmentScales", assessmentScales.join(","));
    if (servicePrograms?.length) params.append("servicePrograms", servicePrograms.join(","));
    if (serviceDescriptions?.length) params.append("serviceDescriptions", serviceDescriptions.join(","));
    if (providers?.length) params.append("providers", providers.join(","));
    if (caseManagers?.length) params.append("caseManagers", caseManagers.join(","));
    if (assessmentStaff?.length) params.append("assessmentStaff", assessmentStaff.join(","));
    if (severityIndicators?.length) params.append("severityIndicators", severityIndicators.join(","));
    if (dateRanges?.length) params.append("dateYears", dateRanges.join(","));

    const headers = await getHeaders(idToken);
    const response = await fetch(`/browse?${params.toString()}`, {
        method: "GET",
        headers
    });

    if (!response.ok) {
        throw new Error(`Failed to browse notes: ${response.statusText}`);
    }

    return response.json();
}
// Patient Brief API
export async function getPatientBrief(consumerId: string, idToken?: string): Promise<PatientBriefResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/patient-brief/${encodeURIComponent(consumerId)}`, {
        method: "GET",
        headers
    });

    if (!response.ok) {
        throw new Error(`Failed to get patient brief: ${response.statusText}`);
    }

    return response.json();
}

export async function getPatientTimeline(consumerId: string, months?: number, category?: string, idToken?: string): Promise<PatientTimelineResponse> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    if (months) params.append("months", months.toString());
    if (category) params.append("category", category);

    const url = `/patient-timeline/${encodeURIComponent(consumerId)}${params.toString() ? `?${params}` : ""}`;
    const response = await fetch(url, {
        method: "GET",
        headers
    });

    if (!response.ok) {
        throw new Error(`Failed to get patient timeline: ${response.statusText}`);
    }

    return response.json();
}
export async function getPatientDemographics(consumerId: string, token?: string): Promise<PatientDemographics> {
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(`/patient-demographics/${encodeURIComponent(consumerId)}`, { headers });
    if (!response.ok) throw new Error(`Failed to fetch demographics: ${response.statusText}`);
    return response.json();
}

export async function getPatientCaseReview(consumerId: string, token?: string, noteType?: string): Promise<PatientCaseReview> {
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const params = noteType ? `?noteType=${encodeURIComponent(noteType)}` : "";
    const response = await fetch(`/patient-case-review/${encodeURIComponent(consumerId)}${params}`, { headers });
    if (!response.ok) throw new Error(`Failed to fetch note: ${response.statusText}`);
    return response.json();
}

export async function getPatientAssessmentSources(consumerId: string, token?: string): Promise<{ sources: { noteType: string; count: number }[] }> {
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(`/patient-assessment-sources/${encodeURIComponent(consumerId)}`, { headers });
    if (!response.ok) throw new Error(`Failed to fetch assessment sources: ${response.statusText}`);
    return response.json();
}
export interface PatientSearchResult {
    patId: string;
    patientName: string;
}

export async function searchPatients(query: string, idToken?: string): Promise<PatientSearchResult[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`/patient-search?q=${encodeURIComponent(query)}`, {
        method: "GET",
        headers
    });
    if (!response.ok) {
        throw new Error(`Patient search failed: ${response.statusText}`);
    }
    const data = await response.json();
    return data.results ?? [];
}

export async function logPageAccess(page: string, userId: string, ehrUserId: string, patientId: string, action?: string, question?: string): Promise<void> {
    const headers = await getHeaders("");
    await fetch("/audit_log", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
            page,
            user_id: userId,
            ehr_user_id: ehrUserId,
            patient_id: patientId,
            action: action || "page_view",
            question: question,
            timestamp: Date.now()
        })
    });
}
export async function logDevChoice(type: string, details: Record<string, unknown>, patientId?: string): Promise<void> {
    try {
        await fetch(`${BACKEND_URI}/api/dev-log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ type, details, patient_id: patientId })
        });
    } catch (e) {
        console.error("Dev log failed:", e);
    }
}
